/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: <explanation> */
"use client";
import { Dialog } from "@base-ui/react";
import { Avatar } from "@base-ui/react/avatar";
import { Menu } from "@base-ui/react/menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import verified from "#/assets/badge/verified-badge.svg";
import { uploadFile } from "@/actions/file.action";
import {
  deleteReplyAction,
  getReplyReplies,
  postReplyReply,
} from "@/actions/forum.action";
import ElipsisIcon from "@/components/icons/elipsis";
import { Button } from "@/components/ui/button";
import TopicReplyEditor from "./topic-reply-editor";
import TopicReplyLikeButton from "./topic-reply-like-button";
import type { Reply } from "./types";

type CurrentUser = { name: string; surname: string; imageUrl?: string };

interface NestedReplyCardProps {
  reply: Reply;
  queryKey: string[];
  topicId: string;
}

const NestedReplyCard = ({ reply, queryKey, topicId }: NestedReplyCardProps) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const queryClient = useQueryClient();

  const initials = reply.userName
    .split(" ")
    .map((n) => n[0])
    .join("");

  const { mutate: deleteMutate, isPending: isDeletePending } = useMutation({
    mutationFn: () => deleteReplyAction(reply.id),
    onSuccess: (res) => {
      if (res.isSuccess) {
        setDeleteOpen(false);
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries({ queryKey: ["forum-replies", topicId] });
      }
    },
  });

  return (
    <div className="py-3 flex gap-3">
      <Avatar.Root className="inline-flex size-8 items-center justify-center overflow-hidden rounded-full bg-gray-100 shrink-0">
        {reply.userAvatar && (
          <Avatar.Image
            className="size-full object-cover"
            src={reply.userAvatar}
          />
        )}
        <Avatar.Fallback className="flex size-full items-center justify-center text-xs font-medium text-gray-700">
          {initials}
        </Avatar.Fallback>
      </Avatar.Root>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          <div>
            <span className="inline-flex items-center gap-1 text-sm font-semibold">
              {reply.userName}
              {reply.isVerified && (
                <Image alt="verified" height={14} src={verified} width={14} />
              )}
              <span className="text-xs font-normal text-neutral-400 ml-1">
                {reply.createdAtLabel}
              </span>
            </span>
            <p className="text-xs text-neutral-500">
              {reply.userTitle ?? "Üye"}
            </p>
          </div>
          {reply.isOwner && (
            <Menu.Root>
              <Menu.Trigger render={<Button size="icon-sm" variant="ghost" />}>
                <ElipsisIcon className="size-4" />
              </Menu.Trigger>
              <Menu.Portal>
                <Menu.Positioner align="end" sideOffset={4}>
                  <Menu.Popup className="min-w-40 rounded-xl border border-neutral-200 bg-white shadow-lg py-1 outline-none z-50">
                    <Menu.Item
                      className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 cursor-pointer hover:bg-red-50 data-highlighted:bg-red-50 outline-none"
                      onClick={() => setDeleteOpen(true)}
                    >
                      <Trash className="size-4" />
                      Sil
                    </Menu.Item>
                  </Menu.Popup>
                </Menu.Positioner>
              </Menu.Portal>
            </Menu.Root>
          )}
        </div>

        <div
          className="text-sm mt-1 text-neutral-700 leading-relaxed prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: reply.text }}
        />

        <div className="flex items-center gap-2 mt-2">
          <TopicReplyLikeButton
            initialCount={reply.likeCount}
            initialLiked={reply.isLiked}
            queryKey={queryKey}
            replyId={reply.id}
          />
        </div>
      </div>

      <Dialog.Root onOpenChange={setDeleteOpen} open={deleteOpen}>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 bg-black/40 transition-all duration-150 data-starting-style:opacity-0 data-ending-style:opacity-0" />
          <Dialog.Popup className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl outline-none transition-all duration-150 data-starting-style:scale-90 data-starting-style:opacity-0 data-ending-style:scale-90 data-ending-style:opacity-0">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  Yanıtı Sil
                </Dialog.Title>
                <Dialog.Close
                  className="text-sm font-medium text-gray-500 hover:text-gray-700 cursor-pointer"
                  render={<button type="button" />}
                >
                  ✕
                </Dialog.Close>
              </div>
              <Dialog.Description className="text-sm text-gray-500">
                Bu yanıtı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
              </Dialog.Description>
              <div className="flex gap-2 justify-end">
                <Dialog.Close
                  render={<Button size="sm" type="button" variant="outline" />}
                >
                  Vazgeç
                </Dialog.Close>
                <Button
                  disabled={isDeletePending}
                  onClick={() => deleteMutate()}
                  size="sm"
                  type="button"
                  variant="destructive"
                >
                  Sil
                </Button>
              </div>
            </div>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

interface Props {
  replyId: string;
  topicId: string;
  replyCount: number;
  isLoggedIn: boolean;
  currentUser?: CurrentUser;
  expanded: boolean;
  onExpand: () => void;
}

const SKELETON = 3;
const TopicNestedReplies = ({
  replyId,
  topicId,
  replyCount,
  isLoggedIn,
  currentUser,
  expanded,
  onExpand,
}: Props) => {
  const queryClient = useQueryClient();
  const queryKey = ["forum-nested-replies", replyId];

  const { data, isFetching } = useQuery({
    enabled: expanded,
    queryFn: async () => {
      const res = await getReplyReplies(replyId);
      return res.isSuccess ? res.result : null;
    },
    queryKey,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ html, images }: { html: string; images: File[] }) => {
      let imageUrls: string[] | undefined;
      if (images.length > 0) {
        const results = await Promise.all(
          images.map((file) => uploadFile(file, "forum/replies")),
        );
        const urls = results.flatMap((r) => { return r.isSuccess ? [r.result.fileUrl] : []; });
        if (urls.length > 0) { imageUrls = urls; }
      }
      return postReplyReply(topicId, replyId, html, imageUrls);
    },
    onSuccess: (res) => {
      if (res.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries({ queryKey: ["forum-replies", topicId] });
      }
    },
  });

  if (!expanded) {
    return (
      <Button
        className="mt-2 text-brand-blue-500 border-brand-blue-200"
        onClick={onExpand}
        size="sm"
        variant="outline"
      >
        Tümünü Gör ({replyCount})
      </Button>
    );
  }

  return (
    <div className="mt-2 pl-4 border-l-2 border-neutral-100">
      {isFetching && (
        <div className="space-y-3 py-2">
          {Array.from({ length: Math.min(replyCount, SKELETON) }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <div className="flex gap-3" key={i}>
              <div className="size-8 rounded-full bg-neutral-200 animate-pulse shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-24 bg-neutral-200 animate-pulse rounded" />
                <div className="h-3 w-full bg-neutral-200 animate-pulse rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {data?.data.map((reply) => (
        <NestedReplyCard key={reply.id} queryKey={queryKey} reply={reply} topicId={topicId} />
      ))}

      {isLoggedIn && (
        <div className="py-2">
          <TopicReplyEditor
            isPending={isPending}
            onCancel={() => void 0}
            onSubmit={(html, images) => mutate({ html, images })}
            placeholder="Yanıtını yaz..."
          />
        </div>
      )}
    </div>
  );
};

export default TopicNestedReplies;
